import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, IMAGES } from "@/data";
import { useBooking } from "@/components/booking/BookingContext";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openBooking } = useBooking();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow] duration-500"
      style={{
        backgroundColor: scrolled ? "rgba(251,246,243,0.82)" : "transparent",
        backdropFilter: scrolled ? "blur(18px)" : "none",
        borderBottom: scrolled
          ? "1px solid var(--border-light)"
          : "1px solid transparent",
      }}
    >
      <nav className="mx-auto max-w-[1400px] px-6 md:px-10 h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center"
        >
          <img
            src={IMAGES.logo}
            alt="ProExec Virtual Solutions"
            className="h-12 md:h-14 w-auto"
          />
        </a>

        <div className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="link-underline text-sm tracking-wide"
              style={{ color: "var(--text-secondary)" }}
            >
              {l.label}
            </a>
          ))}
        </div>

        <button
          onClick={openBooking}
          data-testid="nav-cta"
          className="hidden md:inline-flex items-center rounded-full px-6 py-2.5 text-sm font-medium transition-colors duration-300"
          style={{ backgroundColor: "var(--mocha-brown)", color: "#FBF6F3" }}
        >
          Book a consult
        </button>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden p-2"
          onClick={() => setOpen((v) => !v)}
          style={{ color: "var(--text-primary)" }}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden"
            style={{ backgroundColor: "rgba(251,246,243,0.97)" }}
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-serif text-2xl"
                  style={{ color: "var(--text-primary)" }}
                >
                  {l.label}
                </a>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  openBooking();
                }}
                className="mt-2 inline-flex justify-center rounded-full px-6 py-3 text-sm"
                style={{ backgroundColor: "var(--mocha-brown)", color: "#FBF6F3" }}
              >
                Book a consult
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
